import api from 'api/api'
import { useBsvExchangeRate } from 'api/useBsvExchangeRate'
import ReactECharts from 'echarts-for-react'
import { SATOSHIS } from 'lib/bsv-util'
import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import styles from './metrics-viewer.module.scss'

export const TREECHAT_DEVS = ['arghzero', 'metamitya']

// Local storage key for metrics state
const METRICS_STATE_KEY = 'metricsViewerState'

// Default state values
const getDefaultState = () => ({
    dateRange: {
        startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 90 days ago
        endDate: new Date().toISOString().split('T')[0], // today
    },
    showDaily: true,
    excludeTreechatDevs: true,
    activeTab: 'upvalues',
})

// Load state from local storage
const loadStateFromStorage = () => {
    try {
        const savedState = localStorage.getItem(METRICS_STATE_KEY)
        return savedState ? JSON.parse(savedState) : null
    } catch (error) {
        console.error('Error loading metrics state from storage:', error)
        return null
    }
}

// Save state to local storage
const saveStateToStorage = state => {
    try {
        localStorage.setItem(METRICS_STATE_KEY, JSON.stringify(state))
    } catch (error) {
        console.error('Error saving metrics state to storage:', error)
    }
}

/**
 * MetricsViewer component for displaying usage metrics
 */
const MetricsViewer = ({ modalParams, closeModal }) => {
    const location = useLocation()

    // Parse initial state from URL if available, fallback to local storage, then defaults
    const params = queryString.parse(location.search)
    const savedState = loadStateFromStorage()

    const [activeTab, setActiveTab] = useState(params.tab || savedState?.activeTab || 'upvalues')
    const [isLoading, setIsLoading] = useState(true)
    const [metricsData, setMetricsData] = useState(null)
    const [error, setError] = useState(null)
    const [dateRange, setDateRange] = useState({
        startDate:
            params.startDate ||
            savedState?.dateRange?.startDate ||
            new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate:
            params.endDate ||
            savedState?.dateRange?.endDate ||
            new Date().toISOString().split('T')[0],
    })
    const [showDaily, setShowDaily] = useState(
        params.showDaily === 'true' ||
            (params.showDaily === undefined && savedState?.showDaily) ||
            true,
    )
    const [excludeTreechatDevs, setExcludeTreechatDevs] = useState(
        params.excludeTreechatDevs === 'true' ||
            (params.excludeTreechatDevs === undefined && savedState?.excludeTreechatDevs) ||
            true,
    )
    const [refreshTrigger, setRefreshTrigger] = useState(0)

    // Get the exchange rate at the component level
    const exchangeRate = useBsvExchangeRate()

    // Safely convert sats to USD
    const satsToDollars = sats => {
        if (!sats || !exchangeRate) return null
        return (sats / SATOSHIS) * exchangeRate
    }

    // Format USD value
    const formatUsdValue = amount => {
        if (amount === null || typeof amount === 'undefined') return 'N/A' // Check specifically for null or undefined
        // Handle very small non-zero numbers, ensuring 0 itself is formatted correctly
        if (amount !== 0 && amount < 0.001 && amount > -0.001) {
            return amount > 0 ? '< $0.001' : '> -$0.001'
        }
        return `$${amount.toFixed(3)}`
    }

    // Update URL and local storage when state changes
    useEffect(() => {
        const currentState = {
            dateRange,
            showDaily,
            excludeTreechatDevs,
            activeTab,
        }

        // Save to local storage
        saveStateToStorage(currentState)

        // Update URL without causing navigation
        const queryParams = queryString.stringify({
            tab: activeTab,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            showDaily: showDaily.toString(),
            excludeTreechatDevs: excludeTreechatDevs.toString(),
        })

        const newUrl = `/modal/metrics?${queryParams}`
        window.history.replaceState({}, '', newUrl)
    }, [dateRange, showDaily, excludeTreechatDevs, activeTab])

    // Add class to the modal content for styling
    useEffect(() => {
        const modalContent = document.querySelector('.knov-modal-content')
        if (modalContent) {
            modalContent.classList.add('metrics-modal-content')
        }

        return () => {
            const modalContent = document.querySelector('.knov-modal-content')
            if (modalContent) {
                modalContent.classList.remove('metrics-modal-content')
            }
        }
    }, [])

    // Fetch metrics data when parameters change
    useEffect(() => {
        const fetchMetrics = async () => {
            setIsLoading(true)
            setError(null)
            try {
                let data
                const params = {
                    start_date: dateRange.startDate,
                    end_date: dateRange.endDate,
                    daily: showDaily,
                    excluded_users: excludeTreechatDevs ? TREECHAT_DEVS.join(',') : '',
                }

                switch (activeTab) {
                    case 'upvalues':
                        data = await api.getUpvaluesMetrics(params)
                        break
                    case 'posts':
                        data = await api.getPostsMetrics(params)
                        break
                    case 'ai':
                        data = await api.getAiUsageByTypeMetrics(params)

                        // Also fetch the model data for LLM and image gen model charts
                        const modelData = await api.getAiUsageByModelMetrics(params)

                        data.ai_usage_by_model = modelData.metrics
                        break
                    case 'signups':
                        data = await api.getSignupsMetrics(params)
                        break
                    case 'pnl':
                        data = await api.getFeeChargePnlMetrics(params)
                        break
                    default:
                        data = await api.getUpvaluesMetrics(params)
                }

                setMetricsData(data)
            } catch (err) {
                console.error(`Error fetching ${activeTab} metrics:`, err)
                setError(`Failed to load ${activeTab} metrics. Please try again later.`)
            } finally {
                setIsLoading(false)
            }
        }

        if (dateRange.startDate && dateRange.endDate) {
            fetchMetrics()
        }
    }, [dateRange, showDaily, excludeTreechatDevs, activeTab, refreshTrigger])

    const handleDateChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value,
        }))
    }

    const handleRefresh = () => {
        // Increment the refreshTrigger to force a re-fetch
        setRefreshTrigger(prev => prev + 1)
    }

    const handleReset = () => {
        const defaultState = getDefaultState()
        setDateRange(defaultState.dateRange)
        setShowDaily(defaultState.showDaily)
        setExcludeTreechatDevs(defaultState.excludeTreechatDevs)
        setActiveTab(defaultState.activeTab)
    }

    // Handle tab changes
    const handleTabChange = tab => {
        setActiveTab(tab)
    }

    // Handle checkbox changes
    const handleShowDailyChange = () => {
        setShowDaily(prev => !prev)
    }

    const handleExcludeDevsChange = () => {
        setExcludeTreechatDevs(prev => !prev)
    }

    const renderUpvaluesMetrics = () => {
        const upvaluesData = metricsData?.upvalues

        if (!upvaluesData) return <p>No upvalues data available</p>

        // Convert satoshis to USD - do this calculation here instead of in the render
        const totalFeeUsd = satsToDollars(upvaluesData.total_fee)
        const avgFeeUsd = satsToDollars(upvaluesData.average_fee)

        // Pre-process daily data to avoid doing calculations in the render loop
        const dailyData =
            upvaluesData.daily?.map(day => ({
                ...day,
                totalUsd: satsToDollars(day.total),
                averageUsd: satsToDollars(day.average),
            })) || []

        // Calculate trend line data for upvalues metrics
        const totalData = dailyData.map(day => day.total) || []
        const countData = dailyData.map(day => day.count) || []
        const totalTrendData = calculateTrendLine(totalData)

        // Prepare chart options for daily data
        const chartOptions = {
            title: {
                text: 'Daily Upvalues',
                left: 'center',
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                    label: {
                        backgroundColor: '#6a7985',
                    },
                },
                formatter: function (params) {
                    let result = `${params[0].axisValue}<br/>`
                    params.forEach(param => {
                        // Skip trend lines in tooltip if the param name contains "Trend"
                        if (!param.seriesName.includes('Trend')) {
                            const value = param.seriesName.includes('Total (sats)')
                                ? param.value.toLocaleString() + ' sats'
                                : param.value
                            result += `${param.marker} ${param.seriesName}: ${value}<br/>`
                        }
                    })
                    return result
                },
            },
            legend: {
                data: ['Total (sats)', 'Total (sats) Trend', 'Count'],
                top: 25,
                textStyle: {
                    fontSize: 12,
                },
                itemGap: 15,
                itemWidth: 25,
                itemHeight: 14,
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: dailyData.map(day => day.date) || [],
            },
            yAxis: [
                {
                    type: 'value',
                    name: 'Sats',
                    position: 'left',
                },
                {
                    type: 'value',
                    name: 'Count',
                    position: 'right',
                },
            ],
            series: [
                {
                    name: 'Count',
                    type: 'bar',
                    data: countData,
                    yAxisIndex: 1,
                    itemStyle: {
                        color: '#91cc75',
                    },
                    z: 1,
                },
                {
                    name: 'Total (sats)',
                    type: 'line',
                    smooth: true,
                    data: totalData,
                    yAxisIndex: 0,
                    itemStyle: {
                        color: '#5470c6',
                    },
                    z: 2,
                },
                {
                    name: 'Total (sats) Trend',
                    type: 'line',
                    smooth: true,
                    symbol: 'none',
                    itemStyle: {
                        color: '#5470c6',
                        opacity: 0.7,
                    },
                    lineStyle: {
                        type: 'dashed',
                        color: '#5470c6',
                        width: 2,
                        opacity: 0.7,
                    },
                    z: 3,
                    data: totalTrendData,
                    yAxisIndex: 0,
                },
            ],
        }

        return (
            <>
                <div className={styles.metricsOverview}>
                    <div className={styles.metricCard}>
                        <h3>Total Upvalue Fee</h3>
                        <p className={styles.metricValue}>
                            {upvaluesData.total_fee.toLocaleString()} sats
                        </p>
                        <p className={styles.metricSubvalue}>{formatUsdValue(totalFeeUsd)}</p>
                    </div>
                    <div className={styles.metricCard}>
                        <h3>Upvalue Count</h3>
                        <p className={styles.metricValue}>
                            {upvaluesData.upvalue_count.toLocaleString()}
                        </p>
                    </div>
                    <div className={styles.metricCard}>
                        <h3>Average Fee</h3>
                        <p className={styles.metricValue}>
                            {upvaluesData.average_fee.toLocaleString()} sats
                        </p>
                        <p className={styles.metricSubvalue}>{formatUsdValue(avgFeeUsd)}</p>
                    </div>
                </div>

                {showDaily && dailyData.length > 0 && (
                    <>
                        <div className={styles.chartSection}>
                            <ReactECharts
                                option={chartOptions}
                                style={{ height: '350px', width: '100%' }}
                            />
                        </div>
                        <div className={styles.dailyData}>
                            <h3>Daily Upvalues</h3>
                            <table className={styles.dataTable}>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Total (sats)</th>
                                        <th>Total (USD)</th>
                                        <th>Count</th>
                                        <th>Average (sats)</th>
                                        <th>Average (USD)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dailyData.map((day, index) => (
                                        <tr key={index}>
                                            <td>{day.date}</td>
                                            <td>{day.total.toLocaleString()}</td>
                                            <td>{formatUsdValue(day.totalUsd)}</td>
                                            <td>{day.count}</td>
                                            <td>{day.average.toLocaleString()}</td>
                                            <td>{formatUsdValue(day.averageUsd)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </>
        )
    }

    const renderPostsMetrics = () => {
        const postsData = metricsData?.posts

        if (!postsData) return <p>No posts data available</p>

        // Calculate trend line data
        const dailyData = postsData.daily?.map(day => day.count) || []
        const trendData = calculateTrendLine(dailyData)

        // Prepare chart options for daily posts
        const chartOptions = {
            title: {
                text: 'Daily Posts',
                left: 'center',
            },
            tooltip: {
                trigger: 'axis',
            },
            legend: {
                data: ['Posts', 'Trend'],
                top: 25,
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                data: postsData.daily?.map(day => day.date) || [],
                boundaryGap: false,
            },
            yAxis: {
                type: 'value',
                name: 'Posts',
            },
            series: [
                {
                    name: 'Posts',
                    type: 'line',
                    data: dailyData,
                    areaStyle: {},
                    smooth: true,
                },
                {
                    name: 'Trend',
                    type: 'line',
                    data: trendData,
                    smooth: true,
                    symbol: 'none',
                    lineStyle: {
                        type: 'dashed',
                    },
                },
            ],
        }

        return (
            <div className={styles.metricsContent}>
                <div className={`${styles.metricsOverview} ${styles.singleCardOverview}`}>
                    <div className={styles.metricCard}>
                        <h3>Total Posts</h3>
                        <p className={styles.metricValue}>{postsData.total_count}</p>
                    </div>
                </div>

                {showDaily && postsData.daily && postsData.daily.length > 0 && (
                    <div className={styles.chartSection}>
                        <ReactECharts option={chartOptions} style={{ height: '400px' }} />
                        <div className={styles.dailyData}>
                            <h3>Daily Posts Table</h3>
                            <table className={styles.dataTable}>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {postsData.daily.map((day, index) => (
                                        <tr key={index}>
                                            <td>{day.date}</td>
                                            <td>{day.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    // Helper function to calculate trend line
    const calculateTrendLine = data => {
        const n = data.length
        if (n < 2) return data

        // Calculate the slope and intercept using least squares method
        let sumX = 0
        let sumY = 0
        let sumXY = 0
        let sumXX = 0

        for (let i = 0; i < n; i++) {
            sumX += i
            sumY += data[i]
            sumXY += i * data[i]
            sumXX += i * i
        }

        const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX)
        const intercept = (sumY - slope * sumX) / n

        // Generate trend line points
        return data.map((_, i) => slope * i + intercept)
    }

    const renderAiUsageMetrics = () => {
        const aiData = metricsData?.ai_usage_by_type || metricsData
        const modelData = metricsData?.ai_usage_by_model || []

        if (!aiData || (!aiData.by_provider && !aiData.by_type)) {
            return <p>No AI usage data available</p>
        }

        // Get all AI usage data with filtering applied
        const allProviderData = aiData.by_provider || []

        // Ensure the public property is a boolean
        const normalizedData = allProviderData.map(item => ({
            ...item,
            public: !!item.public, // Convert to boolean
        }))

        // Filtered data based on public/private selection
        const filteredData = normalizedData.filter(item => {
            if (item.public) return true
            if (!item.public) return true
            return false
        })

        // Separate data into public and private
        const publicData = normalizedData.filter(item => item.public === true)
        const privateData = normalizedData.filter(item => item.public === false)

        // Create data for total usage by type (LLM vs Image)
        const totalTypeData = []

        const totalLlmCount = normalizedData
            .filter(item => item.type === 'text')
            .reduce((sum, item) => sum + item.usage_count, 0)

        const totalImageCount = normalizedData
            .filter(item => item.type === 'image')
            .reduce((sum, item) => sum + item.usage_count, 0)

        if (totalLlmCount > 0) totalTypeData.push({ name: 'LLM', value: totalLlmCount })
        if (totalImageCount > 0)
            totalTypeData.push({ name: 'Image Generation', value: totalImageCount })

        // Create data for public usage by type (LLM vs Image)
        const publicTypeData = []
        const publicLlmCount = publicData
            .filter(item => item.type === 'text')
            .reduce((sum, item) => sum + item.usage_count, 0)

        const publicImageCount = publicData
            .filter(item => item.type === 'image')
            .reduce((sum, item) => sum + item.usage_count, 0)

        if (publicLlmCount > 0) publicTypeData.push({ name: 'LLM', value: publicLlmCount })
        if (publicImageCount > 0)
            publicTypeData.push({ name: 'Image Generation', value: publicImageCount })

        // Create data for private usage by type (LLM vs Image)
        const privateTypeData = []
        const privateLlmCount = privateData
            .filter(item => item.type === 'text')
            .reduce((sum, item) => sum + item.usage_count, 0)

        const privateImageCount = privateData
            .filter(item => item.type === 'image')
            .reduce((sum, item) => sum + item.usage_count, 0)

        if (privateLlmCount > 0) privateTypeData.push({ name: 'LLM', value: privateLlmCount })
        if (privateImageCount > 0)
            privateTypeData.push({ name: 'Image Generation', value: privateImageCount })

        // Process model data for image generation and LLM models
        // Aggregate model data by model name and provider type
        const imageGenModelsData = []
        const publicImageGenModelsData = []
        const privateImageGenModelsData = []
        const llmModelsData = []
        const publicLlmModelsData = []
        const privateLlmModelsData = []

        if (modelData && modelData.length > 0) {
            // Create maps to aggregate usage counts by model
            const imageModelsMap = new Map()
            const publicImageModelsMap = new Map()
            const privateImageModelsMap = new Map()
            const llmModelsMap = new Map()
            const publicLlmModelsMap = new Map()
            const privateLlmModelsMap = new Map()

            modelData.forEach(item => {
                // Use type field if available, or model_type field, then fall back to provider-based logic if needed
                const isImageModel =
                    item.type === 'image' ||
                    item.model_type === 'image' ||
                    (item.type === undefined &&
                        item.model_type === undefined &&
                        (item.provider?.toLowerCase() === 'replicate' ||
                            item.provider?.toLowerCase() === 'dall-e' ||
                            (item.provider?.toLowerCase() === 'openai' &&
                                item.model?.toLowerCase().includes('dall'))))

                const model = item.model || 'Unknown'
                // Fix public detection - Be more lenient in how we interpret public status
                // It might be coming back as a string 'true', or a boolean true
                const publicValue = item.public
                const isPublic = publicValue === true || publicValue === 'true' || publicValue === 1

                if (isImageModel) {
                    // Image generation model - total
                    const currentCount = imageModelsMap.get(model) || 0
                    imageModelsMap.set(model, currentCount + item.usage_count)

                    // Image generation model - public/private
                    if (isPublic) {
                        const publicCount = publicImageModelsMap.get(model) || 0
                        publicImageModelsMap.set(model, publicCount + item.usage_count)
                    } else {
                        const privateCount = privateImageModelsMap.get(model) || 0
                        privateImageModelsMap.set(model, privateCount + item.usage_count)
                    }
                } else {
                    // LLM model - total
                    const currentCount = llmModelsMap.get(model) || 0
                    llmModelsMap.set(model, currentCount + item.usage_count)

                    // LLM model - public/private
                    if (isPublic) {
                        const publicCount = publicLlmModelsMap.get(model) || 0
                        publicLlmModelsMap.set(model, publicCount + item.usage_count)
                    } else {
                        const privateCount = privateLlmModelsMap.get(model) || 0
                        privateLlmModelsMap.set(model, privateCount + item.usage_count)
                    }
                }
            })

            // Convert maps to arrays for chart data
            imageModelsMap.forEach((value, key) => {
                imageGenModelsData.push({ name: key, value })
            })

            publicImageModelsMap.forEach((value, key) => {
                publicImageGenModelsData.push({ name: key, value })
            })

            privateImageModelsMap.forEach((value, key) => {
                privateImageGenModelsData.push({ name: key, value })
            })

            llmModelsMap.forEach((value, key) => {
                llmModelsData.push({ name: key, value })
            })

            publicLlmModelsMap.forEach((value, key) => {
                publicLlmModelsData.push({ name: key, value })
            })

            privateLlmModelsMap.forEach((value, key) => {
                privateLlmModelsData.push({ name: key, value })
            })
        }

        // Calculate totals for display under the charts
        const totalImageGenCount = imageGenModelsData.reduce((sum, item) => sum + item.value, 0)
        const totalPublicImageGenCount = publicImageGenModelsData.reduce(
            (sum, item) => sum + item.value,
            0,
        )
        const totalPrivateImageGenCount = privateImageGenModelsData.reduce(
            (sum, item) => sum + item.value,
            0,
        )

        const totalLlmModelCount = llmModelsData.reduce((sum, item) => sum + item.value, 0)
        const totalPublicLlmModelCount = publicLlmModelsData.reduce(
            (sum, item) => sum + item.value,
            0,
        )
        const totalPrivateLlmModelCount = privateLlmModelsData.reduce(
            (sum, item) => sum + item.value,
            0,
        )

        // Chart options for total type usage (LLM vs Image)
        const totalTypeChartOptions = {
            title: {
                text: 'All AI Usage by Type',
                left: 'center',
            },
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)',
                confine: true,
                textStyle: {
                    fontSize: 12,
                },
                padding: [5, 10],
                extraCssText: 'max-width: 170px; white-space: normal;',
            },
            series: [
                {
                    type: 'pie',
                    radius: ['40%', '70%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                    label: {
                        show: true,
                        position: 'outside',
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '16',
                            fontWeight: 'bold',
                        },
                    },
                    data: totalTypeData,
                },
            ],
        }

        // Chart options for public type usage (LLM vs Image)
        const publicTypeChartOptions = {
            title: {
                text: 'Public AI Usage by Type',
                left: 'center',
            },
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)',
                confine: true,
                textStyle: {
                    fontSize: 12,
                },
                padding: [5, 10],
                extraCssText: 'max-width: 170px; white-space: normal;',
            },
            series: [
                {
                    type: 'pie',
                    radius: ['40%', '70%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                    label: {
                        show: true,
                        position: 'outside',
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '16',
                            fontWeight: 'bold',
                        },
                    },
                    data: publicTypeData,
                },
            ],
        }

        // Chart options for private type usage (LLM vs Image)
        const privateTypeChartOptions = {
            title: {
                text: 'Private AI Usage by Type',
                left: 'center',
            },
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)',
                confine: true,
                textStyle: {
                    fontSize: 12,
                },
                padding: [5, 10],
                extraCssText: 'max-width: 170px; white-space: normal;',
            },
            series: [
                {
                    type: 'pie',
                    radius: ['40%', '70%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                    label: {
                        show: true,
                        position: 'outside',
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '16',
                            fontWeight: 'bold',
                        },
                    },
                    data: privateTypeData,
                },
            ],
        }

        // Chart options for Image Generation Models - All
        const imageGenModelsChartOptions = {
            title: {
                text: 'All Image Generations',
                left: 'center',
            },
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)',
                confine: true,
                textStyle: {
                    fontSize: 12,
                },
                padding: [5, 10],
                extraCssText: 'max-width: 170px; white-space: normal;',
            },
            series: [
                {
                    type: 'pie',
                    radius: ['40%', '70%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                    label: {
                        show: true,
                        position: 'outside',
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '16',
                            fontWeight: 'bold',
                        },
                    },
                    data: imageGenModelsData,
                },
            ],
        }

        // Chart options for Image Generation Models - Public
        const publicImageGenModelsChartOptions = {
            title: {
                text: 'Public Image Generations',
                left: 'center',
            },
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)',
                confine: true,
                textStyle: {
                    fontSize: 12,
                },
                padding: [5, 10],
                extraCssText: 'max-width: 170px; white-space: normal;',
            },
            series: [
                {
                    type: 'pie',
                    radius: ['40%', '70%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                    label: {
                        show: true,
                        position: 'outside',
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '16',
                            fontWeight: 'bold',
                        },
                    },
                    data: publicImageGenModelsData,
                },
            ],
        }

        // Chart options for Image Generation Models - Private
        const privateImageGenModelsChartOptions = {
            title: {
                text: 'Private Image Generations',
                left: 'center',
            },
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)',
                confine: true,
                textStyle: {
                    fontSize: 12,
                },
                padding: [5, 10],
                extraCssText: 'max-width: 170px; white-space: normal;',
            },
            series: [
                {
                    type: 'pie',
                    radius: ['40%', '70%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                    label: {
                        show: true,
                        position: 'outside',
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '16',
                            fontWeight: 'bold',
                        },
                    },
                    data: privateImageGenModelsData,
                },
            ],
        }

        // Chart options for LLM Models - All
        const llmModelsChartOptions = {
            title: {
                text: 'All LLM Models',
                left: 'center',
            },
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)',
                confine: true,
                textStyle: {
                    fontSize: 12,
                },
                padding: [5, 10],
                extraCssText: 'max-width: 170px; white-space: normal;',
            },
            series: [
                {
                    type: 'pie',
                    radius: ['40%', '70%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                    label: {
                        show: true,
                        position: 'outside',
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '16',
                            fontWeight: 'bold',
                        },
                    },
                    data: llmModelsData,
                },
            ],
        }

        // Chart options for LLM Models - Public
        const publicLlmModelsChartOptions = {
            title: {
                text: 'Public LLM Models',
                left: 'center',
            },
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)',
                confine: true,
                textStyle: {
                    fontSize: 12,
                },
                padding: [5, 10],
                extraCssText: 'max-width: 170px; white-space: normal;',
            },
            series: [
                {
                    type: 'pie',
                    radius: ['40%', '70%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                    label: {
                        show: true,
                        position: 'outside',
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '16',
                            fontWeight: 'bold',
                        },
                    },
                    data: publicLlmModelsData,
                },
            ],
        }

        // Chart options for LLM Models - Private
        const privateLlmModelsChartOptions = {
            title: {
                text: 'Private LLM Models',
                left: 'center',
            },
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)',
                confine: true,
                textStyle: {
                    fontSize: 12,
                },
                padding: [5, 10],
                extraCssText: 'max-width: 170px; white-space: normal;',
            },
            series: [
                {
                    type: 'pie',
                    radius: ['40%', '70%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                    label: {
                        show: true,
                        position: 'outside',
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '16',
                            fontWeight: 'bold',
                        },
                    },
                    data: privateLlmModelsData,
                },
            ],
        }

        return (
            <div className={styles.metricsContent}>
                {/* Row 1: Main AI type distribution - all three charts side by side */}
                <div className={styles.chartSectionRow}>
                    <div className={styles.chartSectionThird}>
                        <ReactECharts
                            option={totalTypeChartOptions}
                            style={{ width: '100%', height: '300px' }}
                        />
                        <div className={styles.totalCount}>
                            Total AI usage: {(totalLlmCount + totalImageCount).toLocaleString()}
                        </div>
                    </div>

                    <div className={styles.chartSectionThird}>
                        <ReactECharts
                            option={publicTypeChartOptions}
                            style={{ width: '100%', height: '300px' }}
                        />
                        <div className={styles.totalCount}>
                            Total public AI usage:{' '}
                            {(publicLlmCount + publicImageCount).toLocaleString()}
                        </div>
                    </div>

                    <div className={styles.chartSectionThird}>
                        <ReactECharts
                            option={privateTypeChartOptions}
                            style={{ width: '100%', height: '300px' }}
                        />
                        <div className={styles.totalCount}>
                            Total private AI usage:{' '}
                            {(privateLlmCount + privateImageCount).toLocaleString()}
                        </div>
                    </div>
                </div>

                {/* Row 2: Image Generation Models - All/Public/Private */}
                {imageGenModelsData.length > 0 && (
                    <div className={styles.chartSectionRow}>
                        <div className={styles.chartSectionThird}>
                            <ReactECharts
                                option={imageGenModelsChartOptions}
                                style={{ width: '100%', height: '300px' }}
                            />
                            <div className={styles.totalCount}>
                                Total image generations: {totalImageGenCount.toLocaleString()}
                            </div>
                        </div>

                        <div className={styles.chartSectionThird}>
                            {publicImageGenModelsData.length > 0 ? (
                                <>
                                    <ReactECharts
                                        option={publicImageGenModelsChartOptions}
                                        style={{ width: '100%', height: '300px' }}
                                    />
                                    <div className={styles.totalCount}>
                                        Total public image generations:{' '}
                                        {totalPublicImageGenCount.toLocaleString()}
                                    </div>
                                </>
                            ) : (
                                <div className={styles.emptyChartPlaceholder}>
                                    <p>No public image generation data available</p>
                                </div>
                            )}
                        </div>

                        <div className={styles.chartSectionThird}>
                            {privateImageGenModelsData.length > 0 ? (
                                <>
                                    <ReactECharts
                                        option={privateImageGenModelsChartOptions}
                                        style={{ width: '100%', height: '300px' }}
                                    />
                                    <div className={styles.totalCount}>
                                        Total private image generations:{' '}
                                        {totalPrivateImageGenCount.toLocaleString()}
                                    </div>
                                </>
                            ) : (
                                <div className={styles.emptyChartPlaceholder}>
                                    <p>No private image generation data available</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Row 3: LLM Models - All/Public/Private */}
                {llmModelsData.length > 0 && (
                    <div className={styles.chartSectionRow}>
                        <div className={styles.chartSectionThird}>
                            <ReactECharts
                                option={llmModelsChartOptions}
                                style={{ width: '100%', height: '300px' }}
                            />
                            <div className={styles.totalCount}>
                                Total LLM usage: {totalLlmModelCount.toLocaleString()}
                            </div>
                        </div>

                        <div className={styles.chartSectionThird}>
                            {publicLlmModelsData.length > 0 ? (
                                <>
                                    <ReactECharts
                                        option={publicLlmModelsChartOptions}
                                        style={{ width: '100%', height: '300px' }}
                                    />
                                    <div className={styles.totalCount}>
                                        Total public LLM usage:{' '}
                                        {totalPublicLlmModelCount.toLocaleString()}
                                    </div>
                                </>
                            ) : (
                                <div className={styles.emptyChartPlaceholder}>
                                    <p>No public LLM data available</p>
                                </div>
                            )}
                        </div>

                        <div className={styles.chartSectionThird}>
                            {privateLlmModelsData.length > 0 ? (
                                <>
                                    <ReactECharts
                                        option={privateLlmModelsChartOptions}
                                        style={{ width: '100%', height: '300px' }}
                                    />
                                    <div className={styles.totalCount}>
                                        Total private LLM usage:{' '}
                                        {totalPrivateLlmModelCount.toLocaleString()}
                                    </div>
                                </>
                            ) : (
                                <div className={styles.emptyChartPlaceholder}>
                                    <p>No private LLM data available</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Data table */}
                {filteredData.length > 0 && (
                    <div className={styles.section}>
                        <h3>AI Usage by Provider Table</h3>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th>Provider</th>
                                    <th>Type</th>
                                    <th>Visibility</th>
                                    <th>Usage Count</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.provider}</td>
                                        <td>
                                            {item.type === 'image' ? 'Image Generation' : 'LLM'}
                                        </td>
                                        <td>{item.public ? 'Public' : 'Private'}</td>
                                        <td>{item.usage_count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        )
    }

    const renderSignupsMetrics = () => {
        const signupsData = metricsData?.signups

        if (!signupsData) return <p>No signups data available</p>

        // Calculate trend line data
        const dailyData = signupsData.daily?.map(day => day.count) || []
        const trendData = calculateTrendLine(dailyData)

        // Prepare chart options for daily signups
        const chartOptions = {
            title: {
                text: 'Daily Signups',
                left: 'center',
            },
            tooltip: {
                trigger: 'axis',
            },
            legend: {
                data: ['Signups', 'Trend'],
                top: 25,
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                data: signupsData.daily?.map(day => day.date) || [],
                boundaryGap: false,
            },
            yAxis: {
                type: 'value',
                name: 'Signups',
            },
            series: [
                {
                    name: 'Signups',
                    type: 'bar',
                    data: dailyData,
                    itemStyle: {
                        color: '#91cc75',
                    },
                },
                {
                    name: 'Trend',
                    type: 'line',
                    data: trendData,
                    smooth: true,
                    symbol: 'none',
                    lineStyle: {
                        type: 'dashed',
                        color: '#666',
                    },
                },
            ],
        }

        return (
            <div className={styles.metricsContent}>
                <div className={`${styles.metricsOverview} ${styles.singleCardOverview}`}>
                    <div className={styles.metricCard}>
                        <h3>Total Signups</h3>
                        <p className={styles.metricValue}>{signupsData.total_count}</p>
                    </div>
                </div>

                {showDaily && signupsData.daily && signupsData.daily.length > 0 && (
                    <div className={styles.chartSection}>
                        <ReactECharts option={chartOptions} style={{ height: '400px' }} />
                        <div className={styles.dailyData}>
                            <h3>Daily Signups Table</h3>
                            <table className={styles.dataTable}>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {signupsData.daily.map((day, index) => (
                                        <tr key={index}>
                                            <td>{day.date}</td>
                                            <td>{day.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const renderPnlMetrics = () => {
        const pnlMetrics = metricsData?.pnl_metrics

        if (!pnlMetrics) return <p>No PnL data available</p>

        // Overall Summary Cards (These are fine, use pnlMetrics.total_revenue, .total_cost, .total_pnl)
        const overallSummaryCards = (
            <div className={styles.metricsOverview}>
                <div className={styles.metricCard}>
                    <h3>Total Revenue</h3>
                    <p className={styles.metricValue}>{formatUsdValue(pnlMetrics.total_revenue)}</p>
                </div>
                <div className={styles.metricCard}>
                    <h3>Total Cost</h3>
                    <p className={styles.metricValue}>{formatUsdValue(pnlMetrics.total_cost)}</p>
                </div>
                <div className={styles.metricCard}>
                    <h3>Total PnL</h3>
                    <p className={styles.metricValue}>{formatUsdValue(pnlMetrics.total_pnl)}</p>
                </div>
                <div className={styles.metricCard}>
                    <h3>Total Charges Processed</h3>
                    <p className={styles.metricValue}>
                        {pnlMetrics.total_charges_processed?.toLocaleString()}
                    </p>
                </div>
            </div>
        )

        // --- Pie Charts for Public/Private PnL by Model ---
        const privatePnLPieChartOptions = getContextPnLPieChartOptions(
            pnlMetrics.by_model,
            'private_pnl',
            'PnL from Private Usage by Model',
            true, // Only positive PnL
        )
        const publicPnLPieChartOptions = getContextPnLPieChartOptions(
            pnlMetrics.by_model,
            'public_pnl',
            'Loss from Public Usage by Model',
            false, // Show losses (will take absolute for pie chart value)
        )

        const pieChartsSection = pnlMetrics.by_model && pnlMetrics.by_model.length > 0 && (
            <div className={styles.chartSectionRow}>
                <div className={styles.chartSectionHalf}>
                    <ReactECharts
                        option={privatePnLPieChartOptions}
                        style={{ height: '300px', width: '100%' }}
                    />
                    <div className={styles.totalCount}>
                        Total PnL from Private Usage:{' '}
                        {formatUsdValue(
                            pnlMetrics.by_model.reduce((sum, item) => sum + item.private_pnl, 0),
                        )}
                    </div>
                </div>
                <div className={styles.chartSectionHalf}>
                    <ReactECharts
                        option={publicPnLPieChartOptions}
                        style={{ height: '300px', width: '100%' }}
                    />
                    <div className={styles.totalCount}>
                        Total Loss from Public Usage:{' '}
                        {formatUsdValue(
                            pnlMetrics.by_model.reduce((sum, item) => sum + item.public_pnl, 0),
                        )}
                    </div>
                </div>
            </div>
        )

        // --- Daily PnL Line Chart (Existing logic, can be kept or adapted) ---
        const dailyChartData = {
            dates: pnlMetrics.daily?.map(day => day.date) || [],
            revenue: pnlMetrics.daily?.map(day => day.revenue) || [],
            cost: pnlMetrics.daily?.map(day => day.cost) || [],
            pnl: pnlMetrics.daily?.map(day => day.pnl) || [],
        }
        const revenueTrend = calculateTrendLine(dailyChartData.revenue)
        const costTrend = calculateTrendLine(dailyChartData.cost)
        const pnlTrend = calculateTrendLine(dailyChartData.pnl)
        const dailyLineChartOptions = {
            title: { text: 'Daily Overall PnL, Revenue, and Cost', left: 'center' },
            tooltip: {
                trigger: 'axis',
                formatter: function (params) {
                    let result = `${params[0].axisValue}<br/>`
                    params.forEach(param => {
                        if (!param.seriesName.includes('Trend')) {
                            result += `${param.marker} ${param.seriesName}: ${formatUsdValue(
                                param.value,
                            )}<br/>`
                        }
                    })
                    return result
                },
            },
            legend: {
                data: ['Revenue', 'Cost', 'PnL', 'Revenue Trend', 'Cost Trend', 'PnL Trend'],
                top: 25,
            },
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
            xAxis: { type: 'category', boundaryGap: false, data: dailyChartData.dates },
            yAxis: {
                type: 'value',
                name: 'USD',
                axisLabel: { formatter: value => formatUsdValue(value) },
            },
            series: [
                {
                    name: 'Revenue',
                    type: 'line',
                    smooth: true,
                    data: dailyChartData.revenue,
                    itemStyle: { color: '#5470c6' },
                },
                {
                    name: 'Revenue Trend',
                    type: 'line',
                    smooth: true,
                    symbol: 'none',
                    lineStyle: { type: 'dashed', color: '#5470c6', opacity: 0.7 },
                    data: revenueTrend,
                },
                {
                    name: 'Cost',
                    type: 'line',
                    smooth: true,
                    data: dailyChartData.cost,
                    itemStyle: { color: '#ee6666' },
                },
                {
                    name: 'Cost Trend',
                    type: 'line',
                    smooth: true,
                    symbol: 'none',
                    lineStyle: { type: 'dashed', color: '#ee6666', opacity: 0.7 },
                    data: costTrend,
                },
                {
                    name: 'PnL',
                    type: 'line',
                    smooth: true,
                    data: dailyChartData.pnl,
                    itemStyle: { color: '#91cc75' },
                },
                {
                    name: 'PnL Trend',
                    type: 'line',
                    smooth: true,
                    symbol: 'none',
                    lineStyle: { type: 'dashed', color: '#91cc75', opacity: 0.7 },
                    data: pnlTrend,
                },
            ],
        }
        const dailyLineChartSection = showDaily &&
            pnlMetrics.daily &&
            pnlMetrics.daily.length > 0 && (
                <div className={styles.chartSection}>
                    <ReactECharts
                        option={dailyLineChartOptions}
                        style={{ height: '400px', width: '100%' }}
                    />
                </div>
            )

        // --- Grouped Bar Chart for Net PnL by Model (Private vs Public) ---
        const netPnlByModelBarChartOptions = getNetPnlByModelBarChartOptions(pnlMetrics.by_model)
        const netPnlBarChartSection = pnlMetrics.by_model && pnlMetrics.by_model.length > 0 && (
            <div className={styles.chartSection}>
                <ReactECharts
                    option={netPnlByModelBarChartOptions}
                    style={{ height: '400px', width: '100%' }}
                />
            </div>
        )

        // --- PnL by Model Table (Updated) ---
        const pnlByModelTable = pnlMetrics.by_model && pnlMetrics.by_model.length > 0 && (
            <div className={styles.section}>
                <h3>PnL Breakdown by AI Model</h3>
                <table className={styles.dataTable}>
                    <thead>
                        <tr>
                            <th>Model Name</th>
                            <th>Private Revenue</th>
                            <th>Private Cost</th>
                            <th>Private PnL</th>
                            <th>Private Charges</th>
                            <th>Public Revenue</th>
                            <th>Public Cost</th>
                            <th>Public PnL (Loss)</th>
                            <th>Public Charges</th>
                            <th>Net PnL</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pnlMetrics.by_model.map((model_data, index) => (
                            <tr key={index}>
                                <td>{model_data.model_name}</td>
                                <td>{formatUsdValue(model_data.private_revenue)}</td>
                                <td>{formatUsdValue(model_data.private_cost)}</td>
                                <td>{formatUsdValue(model_data.private_pnl)}</td>
                                <td>{model_data.private_charges.toLocaleString()}</td>
                                <td>{formatUsdValue(model_data.public_revenue)}</td>
                                <td>{formatUsdValue(model_data.public_cost)}</td>
                                <td>{formatUsdValue(model_data.public_pnl)}</td>
                                <td>{model_data.public_charges.toLocaleString()}</td>
                                <td>{formatUsdValue(model_data.net_pnl_for_model)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )

        // --- Daily PnL Breakdown Table (Existing, can be kept as is) ---
        const dailyBreakdownTable = showDaily &&
            pnlMetrics.daily &&
            pnlMetrics.daily.length > 0 && (
                <div className={styles.dailyData}>
                    <h3>Daily Overall PnL Breakdown</h3>
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Revenue</th>
                                <th>Cost</th>
                                <th>PnL</th>
                                <th>Charges</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pnlMetrics.daily.map((day, index) => (
                                <tr key={index}>
                                    <td>{day.date}</td>
                                    <td>{formatUsdValue(day.revenue)}</td>
                                    <td>{formatUsdValue(day.cost)}</td>
                                    <td>{formatUsdValue(day.pnl)}</td>
                                    <td>{day.count?.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )

        return (
            <div className={styles.metricsContent}>
                {overallSummaryCards}
                {pieChartsSection}
                {dailyLineChartSection}
                {netPnlBarChartSection}
                {pnlByModelTable}
                {dailyBreakdownTable}
            </div>
        )
    }

    // Helper for context-specific PnL Pie Charts (Private PnL, Public Loss)
    const getContextPnLPieChartOptions = (byModelData, pnlFieldKey, title, positiveOnly) => {
        let chartData = byModelData.map(model => ({
            name: model.model_name,
            value: model[pnlFieldKey],
        }))

        if (positiveOnly) {
            // For private PnL, only show profits
            chartData = chartData.filter(item => item.value > 0)
        } else {
            // For public PnL (losses), take absolute value for pie chart sizing
            chartData = chartData
                .filter(item => item.value < 0)
                .map(item => ({ ...item, value: Math.abs(item.value) }))
        }

        // Sort by value descending to make the pie chart more readable if labels were on
        chartData.sort((a, b) => b.value - a.value)

        return {
            title: { text: title, left: 'center' },
            tooltip: {
                trigger: 'item',
                formatter: params =>
                    `${params.name}: ${formatUsdValue(
                        positiveOnly ? params.value : -params.value,
                    )} (${params.percent}%)`,
                confine: true,
            },
            series: [
                {
                    type: 'pie',
                    radius: ['40%', '70%'],
                    avoidLabelOverlap: true,
                    itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
                    label: { show: false, position: 'outside' },
                    emphasis: { label: { show: true, fontSize: '14', fontWeight: 'bold' } },
                    data: chartData,
                },
            ],
        }
    }

    // Helper for Net PnL by Model Grouped Bar Chart
    const getNetPnlByModelBarChartOptions = byModelData => {
        const modelNames = byModelData.map(m => m.model_name)
        const privatePnlData = byModelData.map(m => m.private_pnl)
        const publicPnlData = byModelData.map(m => m.public_pnl) // These will be negative

        return {
            title: { text: 'Net PnL by Model (Private vs. Public)', left: 'center' },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: function (params) {
                    let result = `${params[0].name}<br/>`
                    params.forEach(param => {
                        result += `${param.marker} ${param.seriesName}: ${formatUsdValue(
                            param.value,
                        )}<br/>`
                    })
                    // Calculate and add Net PnL for the hovered model
                    const modelEntry = byModelData.find(m => m.model_name === params[0].name)
                    if (modelEntry) {
                        result += `<strong>Net PnL: ${formatUsdValue(
                            modelEntry.net_pnl_for_model,
                        )}</strong><br/>`
                    }
                    return result
                },
            },
            legend: { data: ['PnL from Private Usage', 'PnL from Public Usage'], top: 25 },
            grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true }, // Increased bottom margin for labels
            xAxis: { type: 'category', data: modelNames, axisLabel: { interval: 0, rotate: 30 } }, // Rotate labels to prevent overlap
            yAxis: {
                type: 'value',
                name: 'USD',
                axisLabel: { formatter: value => formatUsdValue(value) },
            },
            series: [
                {
                    name: 'PnL from Private Usage',
                    type: 'bar',
                    stack: 'pnl', // Stack if you want to show net, but separate bars are better for comparison
                    itemStyle: { color: '#91cc75' }, // Green for profit
                    data: privatePnlData,
                },
                {
                    name: 'PnL from Public Usage',
                    type: 'bar',
                    stack: 'pnl',
                    itemStyle: { color: '#ee6666' }, // Red for loss
                    data: publicPnlData,
                },
            ],
        }
    }

    const renderTabContent = () => {
        if (isLoading) {
            return (
                <div className={styles.loadingContainer}>
                    <div className={styles.spinner}>
                        <i className="fa fa-circle-o-notch fa-spin fa-2x" />
                    </div>
                    <p>Loading metrics data...</p>
                </div>
            )
        }

        if (error) {
            return (
                <div className={styles.errorContainer}>
                    <i className="fa fa-exclamation-triangle" />
                    <p>{error}</p>
                </div>
            )
        }

        switch (activeTab) {
            case 'upvalues':
                return renderUpvaluesMetrics()
            case 'posts':
                return renderPostsMetrics()
            case 'ai':
                return renderAiUsageMetrics()
            case 'signups':
                return renderSignupsMetrics()
            case 'pnl':
                return renderPnlMetrics()
            default:
                return <p>Select a tab to view metrics</p>
        }
    }

    // Helper function to get date range presets
    const getDateRangePreset = preset => {
        const endDate = new Date().toISOString().split('T')[0] // Today
        let startDate

        switch (preset) {
            case 'day':
                // Last 24 hours
                startDate = new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split('T')[0]
                break
            case 'week':
                // Last 7 days
                startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split('T')[0]
                break
            case 'month':
                // Last 30 days
                startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split('T')[0]
                break
            case 'year':
                // Last 365 days
                startDate = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split('T')[0]
                break
            default:
                startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split('T')[0]
        }

        return { startDate, endDate }
    }

    // Apply a preset date range
    const applyDateRangePreset = preset => {
        const newRange = getDateRangePreset(preset)
        setDateRange(newRange)
    }

    // Update the renderDateControls to put quick select buttons in line with date inputs
    const renderDateControls = () => (
        <div className={styles.controls}>
            <div className={styles.dateControlsRow}>
                <div className={styles.dateRange}>
                    <div className={styles.dateInput}>
                        <label>Start Date:</label>
                        <input
                            type="date"
                            value={dateRange.startDate}
                            onChange={e => handleDateChange('startDate', e.target.value)}
                        />
                    </div>
                    <div className={styles.dateInput}>
                        <label>End Date:</label>
                        <input
                            type="date"
                            value={dateRange.endDate}
                            onChange={e => handleDateChange('endDate', e.target.value)}
                        />
                    </div>
                </div>
                <div className={styles.quickSelect}>
                    <span className={styles.quickSelectLabel}>Quick select last:</span>
                    <div className={styles.quickSelectButtons}>
                        <button
                            className={styles.quickSelectButton}
                            onClick={() => applyDateRangePreset('year')}
                        >
                            Year
                        </button>
                        <button
                            className={styles.quickSelectButton}
                            onClick={() => applyDateRangePreset('month')}
                        >
                            Month
                        </button>
                        <button
                            className={styles.quickSelectButton}
                            onClick={() => applyDateRangePreset('week')}
                        >
                            Week
                        </button>
                        <button
                            className={styles.quickSelectButton}
                            onClick={() => applyDateRangePreset('day')}
                        >
                            Day
                        </button>
                    </div>
                </div>
            </div>
            <div className={styles.checkboxControls}>
                <label className={styles.checkboxControl}>
                    <input type="checkbox" checked={showDaily} onChange={handleShowDailyChange} />
                    Show Daily Data
                </label>
                <label className={styles.checkboxControl}>
                    <input
                        type="checkbox"
                        checked={excludeTreechatDevs}
                        onChange={handleExcludeDevsChange}
                    />
                    <span>
                        Exclude Treechat Devs
                        <span className={styles.helperText}>({TREECHAT_DEVS.join(', ')})</span>
                    </span>
                </label>
            </div>
            <div className={styles.buttonGroup}>
                <button className={styles.refreshButton} onClick={handleRefresh}>
                    <i className="fa fa-sync-alt" /> Refresh Data
                </button>
                <button className={styles.resetButton} onClick={handleReset}>
                    <i className="fa fa-undo" /> Reset
                </button>
            </div>
        </div>
    )

    return (
        <div className={styles.container}>
            {renderDateControls()}

            <div className={styles.tabsContainer}>
                <div
                    className={`${styles.tab} ${activeTab === 'upvalues' ? styles.active : ''}`}
                    onClick={() => handleTabChange('upvalues')}
                >
                    Upvalues
                </div>
                <div
                    className={`${styles.tab} ${activeTab === 'signups' ? styles.active : ''}`}
                    onClick={() => handleTabChange('signups')}
                >
                    Signups
                </div>
                <div
                    className={`${styles.tab} ${activeTab === 'posts' ? styles.active : ''}`}
                    onClick={() => handleTabChange('posts')}
                >
                    Posts
                </div>
                <div
                    className={`${styles.tab} ${activeTab === 'ai' ? styles.active : ''}`}
                    onClick={() => handleTabChange('ai')}
                >
                    AI Usage
                </div>
                <div
                    className={`${styles.tab} ${activeTab === 'pnl' ? styles.active : ''}`}
                    onClick={() => handleTabChange('pnl')}
                >
                    PnL
                </div>
            </div>

            <div className={styles.content}>
                {isLoading ? (
                    <div className={styles.loadingContainer}>
                        <div className={styles.spinner}>Loading...</div>
                    </div>
                ) : error ? (
                    <div className={styles.errorContainer}>
                        <p>{error}</p>
                    </div>
                ) : (
                    <div className={styles.metricsContent}>{renderTabContent()}</div>
                )}
            </div>
        </div>
    )
}

export default MetricsViewer
